import React, { useMemo } from 'react'
import styled from 'styled-components'
import DataTable from 'react-data-table-component'
import { Info, Check, AlertCircle, Archive } from 'react-feather'
import { useParams } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { useIntl, FormattedMessage } from 'react-intl'
import { UncontrolledTooltip } from 'reactstrap'
import { useSelector } from 'react-redux'
import useAxios from 'axios-hooks'

import { ScoreMeter } from '~/pages/app/LocationsList/components/ScoreMeter'
import { checklistMetrics } from '~/constants/checklistMetrics'
import { ReactComponent as ArrowUpDown } from '~/assets/img/svg/arrow-up-down.svg'
import { queries } from '~/constants/mediaQuery'

const tableStyle = {
  tableWrapper: {
    style: {
      border: '1px solid #E2E8F0',
      borderRadius: '8px',
    },
  },
  table: {
    style: {
      borderRadius: '8px',
    },
  },
  head: {
    style: {
      height: '40px',
      borderRadius: '8px',
    },
  },
  headCells: {
    style: {
      color: '#4A5568',
      backgroundColor: '#F7FAFC',
      fontWeight: '700',
      height: '40px',
      '&:first-of-type': {
        borderRadius: '8px 0 0 0',
      },
      '&:last-of-type': {
        borderRadius: '0 8px 0 0',
      },
    },
  },
  headRow: {
    style: {
      borderRadius: '8px',
    },
  },
  rows: {
    style: {
      '&:not(:last-of-type)': {
        borderBottomColor: '#E2E8F0',
      },
      '&:last-of-type': {
        borderRadius: '0 0 8px 8px',
      },
    },
  },
}

const IMPACT_TEXT = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
}

const IMPACT_COLORS = {
  0: {
    true: '#f5f5f1',
    false: '#f5f5f1',
    null: '#f5f5f1',
  },
  1: {
    true: '#4FD1C5',
    false: '#319795',
    null: '#319795',
  },
  2: {
    true: '#90CDF4',
    false: '#4299E1',
    null: '#4299E1',
  },
  3: {
    true: '#ABA4F6',
    false: '#7367F0',
    null: '#7367F0',
  },
}

const LoadingData = () => {
  return <Loading></Loading>
}

export const ChecklistForm = ({ location }) => {
  const projectId = useSelector(state => state.user?.selectedProject?.id)
  const { locationId } = useParams()
  const intl = useIntl()

  const [{ data, loading }] = useAxios(
    `/projects/${projectId}/locations/${locationId}/checklist`
  )

  const statusCell = (row, index) => (
    <StatusCell id={`annulled-${index}`} done={row?.done}>
      {row?.done === true ? (
        <>
          <Check size="20" color="#25855A" />
          <FormattedMessage id="Done" />
        </>
      ) : row?.done === false ? (
        <>
          <AlertCircle size="20" color="#FFF5F5" fill="#C53030" />
          <FormattedMessage id="Pending" />
        </>
      ) : (
        <>
          <AlertCircle size="20" color="#F5F7FA" fill="#ADB2BA" />
          <FormattedMessage id="Annulled" />
          <UncontrolledTooltip target={`annulled-${index}`}>
            {intl.formatMessage({
              id: row?.nullReason || 'This item was annulled.',
            })}
          </UncontrolledTooltip>
        </>
      )}
    </StatusCell>
  )

  const impactCell = row => {
    const impact = row?.impact
    const impactText = IMPACT_TEXT[impact]
    const impactColor = IMPACT_COLORS[impact][row?.done]
    return (
      <ImpactCell>
        <AlertCircle size="23" fill={impactColor} color="#fff" />
        <FormattedMessage id={impactText} />
      </ImpactCell>
    )
  }

  const itemCell = row => {
    const Icon = row?.icon || Archive
    return (
      <ItemCell done={row?.done}>
        <div>
          <Icon size="30" />
        </div>
        <div className="item-text">
          <div>
            <FormattedMessage id={row?.name} />
          </div>
          <div>
            <FormattedMessage id={row?.description} />
          </div>
        </div>
      </ItemCell>
    )
  }

  const columns = [
    {
      name: <FormattedMessage id="Status" />,
      selector: 'done',
      sortable: true,
      width: '150px',
      cell: statusCell,
    },
    {
      name: <FormattedMessage id="Item" />,
      selector: 'name',
      sortable: true,
      minWidth: '400px',
      cell: itemCell,
    },
    {
      name: <FormattedMessage id="Impact" />,
      selector: 'impact',
      sortable: true,
      width: '200px',
      cell: impactCell,
    },
  ]

  const tableData = useMemo(() => {
    return checklistMetrics.map(metric => {
      return {
        ...metric,
        done: data?.[metric.id],
      }
    })
  }, [data])

  return (
    <>
      <ChecklistHeaderDiv>
        <div className="location-info">
          <h2>{location?.title}</h2>
          <div>
            {location?.storeCode && (
              <div className="store-code">{location?.storeCode}</div>
            )}
            {location?.storefrontAddress?.addressLines?.[0] && (
              <div className="address-lines">
                {location?.storefrontAddress?.addressLines?.[0]}
                {location?.storefrontAddress?.sublocality &&
                  `, ${location?.storefrontAddress?.sublocality}`}
              </div>
            )}
          </div>
        </div>
        <div className="score-overview">
          <div className="info-icon" id="info-icon">
            <Info color="#4A5568" size={20} />
          </div>
          <UncontrolledTooltip target="info-icon">
            {intl.formatMessage({
              id: 'This score was calculated on',
            })}
            {data?.createdAt
              ? ` ${format(parseISO(data?.createdAt), 'dd/MM/yyyy')} `
              : ''}
          </UncontrolledTooltip>
          <div className="performance-graphics">
            <ScoreMeter score={data?.score || 0} width="170px" />
            <div className="performance-average-score">
              <div>{data?.score || 0}</div>
              <div>/100</div>
            </div>
          </div>
          <div className="performance-text">
            <div className="performance-title">
              {intl.formatMessage({ id: 'Performance of your profile' })}
            </div>
            <div className="performance-description">
              {intl.formatMessage({
                id:
                  'Complete your locations info and keep improving your score to have better results.',
              })}
            </div>
          </div>
        </div>
      </ChecklistHeaderDiv>
      <div>
        <DataTable
          keyField="location"
          className="dataTable-custom"
          highlightOnHover={true}
          pointerOnHover={true}
          data={tableData}
          columns={columns}
          customStyles={tableStyle}
          sortIcon={<ArrowUpDown />}
          noHeader
          progressPending={loading}
          progressComponent={<LoadingData />}
        />
      </div>
    </>
  )
}

const ItemCell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1em;
  padding: 0.3em 0;
  color: ${props => (props?.done ? '#4A5568' : '#718096')};

  .item-text {
    > div:nth-child(1) {
      font-size: 1.2em;
      font-weight: 400;
    }

    > div:nth-child(2) {
      font-size: 1em;
      font-weight: 400;
    }
  }
`

const ImpactCell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1em;

  color: #2d3748;
  font-size: 1em;
  font-weight: 600;
`

const StatusCell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1em;
  padding: 0.3em 0.7em;
  border-radius: 5px;
  width: 100%;

  background-color: ${props =>
    props.done === true
      ? '#F0FFF4'
      : props?.done === false
      ? '#FFF5F5'
      : '#F5F7FA'};
  color: ${props =>
    props.done === true
      ? '#25855A'
      : props?.done === false
      ? '#C53030'
      : '#ADB2BA'};
  font-size: 1em;
  font-weight: 500;
`

const Loading = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 8rem;
  background-color: rgba(0, 0, 0, 0.1);
`

const ChecklistHeaderDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 3em;
  margin-bottom: 3em;

  @media ${queries.less.lg} {
    flex-direction: column-reverse;
  }

  .location-info {
    display: flex;
    flex-direction: column;

    h2 {
      font-size: 2.5em;
      font-weight: 700;
      color: #171923;
    }

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 1em;

      .store-code {
        background-color: #f7fafc;
        padding: 0.2em 0.5em;
        border-radius: 5px;
        font-size: 0.9em;
        font-weight: 400;
        color: #4a5568;
      }

      .address-lines {
        font-size: 1em;
        font-weight: 400;
        color: #718096;
      }
    }
  }

  .score-overview {
    position: relative;
    width: clamp(600px, 650px, 60%);
    background-color: #f7fafc;
    border: 1px solid #cbd5e0;
    border-radius: 5px;
    padding: 2em 2.5em;
    display: flex;
    align-items: center;
    gap: 3em;

    @media ${queries.less.lg} {
      width: 100%;
    }

    @media ${queries.less.sm} {
      flex-direction: column;
    }

    .info-icon {
      position: absolute;
      right: 1em;
      top: 1em;
      cursor: help;
    }

    .performance-graphics {
      height: min-content;
      position: relative;

      &:hover {
        .performance-average-score {
          div:nth-child(1) {
            font-size: 2.7em;
          }
        }
      }
    }

    .performance-average-score {
      display: flex;
      align-items: center;
      position: absolute;
      left: 50%;
      transform: translate(-50%, 0);
      bottom: 0;

      div:nth-child(1) {
        color: #1a202c;
        font-size: 2.5em;
        font-weight: 700;
        transition: font-size 0.2s ease-out;
      }

      div:nth-child(2) {
        color: #718096;
        font-size: 1.2em;
        font-weight: 400;
      }
    }

    .performance-text {
      display: flex;
      flex-direction: column;

      .performance-title {
        color: #1a202c;
        font-size: 1.3em;
        font-weight: 700;
      }

      .performance-description {
        color: #4a5568;
        font-size: 1em;
      }
    }
  }
`
